/**
 * صفحه‌ی «قضاوت تصمیم»: دو تصمیم، یک هدف مشترک، یک جمع‌بندی.
 *
 * ⚠️ **چرا مود مستقل و نه یک prompt داخل مشاوره:** قرارداد خروجی متفاوت است.
 * حکم موافق/مخالف روی یک جفت گزینه بی‌معناست، و نمای نتیجه هم باید مقایسه‌ای
 * باشد نه تک‌گزینه‌ای. گنجاندنش در همان صفحه یعنی هر بخش باید حالت خودش را حدس
 * بزند.
 *
 * مرحله‌های شفاف‌سازی همان مرحله‌های مشاوره‌اند و از همان کامپوننت استفاده
 * می‌کنند: تجربه‌ی کاربر نباید بین دو مود بی‌دلیل عوض شود.
 */

import { Download, FileJson, GitCompareArrows, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Steps } from '@/components/ui/Steps'
import { Banner, Card, PageHeader, SectionHeader } from '@/components/ui/primitives'
import { ClarificationStep } from '@/features/consultation/ClarificationStep'
import { EvidenceView } from '@/features/consultation/EvidenceView'
import { downloadJudgment } from '@/lib/api/endpoints'
import { saveBlob } from '@/lib/format'

import { DecisionPairForm } from './DecisionPairForm'
import { JudgmentResult } from './JudgmentResult'
import { useJudgment } from './useJudgment'

import type { Scope } from '@/lib/api/endpoints'

interface JudgmentPageProps {
  /** در حالت کارگاه لازم است؛ در نسخه‌ی مشتری تعریف نمی‌شود. */
  scope?: Scope
}

export const JudgmentPage = ({ scope }: JudgmentPageProps) => {
  const judgment = useJudgment(scope)
  const { state } = judgment

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<GitCompareArrows className="size-5.5" aria-hidden />}
        eyebrow="قضاوت تصمیم"
        title="مقایسه‌ی دو تصمیم"
        caption="دو راه پیش رو را در برابر یک هدف مشترک بگذارید تا معلوم شود منطق تصمیم‌گیر به کدام نزدیک‌تر است."
        actions={
          state !== null && (
            <Button
              variant="secondary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={judgment.reset}
            >
              مقایسه‌ی بعدی
            </Button>
          )
        }
      />

      <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-surface-raised px-4 py-3 shadow-raised">
        <Steps current={judgment.step} />
      </div>

      {judgment.error !== null && (
        <Banner tone="danger" title="مقایسه کامل نشد">
          {judgment.error.message}
        </Banner>
      )}

      {state === null && (
        <DecisionPairForm onSubmit={judgment.start} isBusy={judgment.isBusy} />
      )}

      {state !== null &&
        state.stage === 'awaiting_clarification' &&
        state.analysis !== null && (
          <ClarificationStep
            questions={state.analysis.clarification_questions}
            onSubmit={judgment.answer}
            onReset={judgment.reset}
            isBusy={judgment.isBusy}
          />
        )}

      {state !== null && state.stage === 'failed' && (
        <Card>
          <SectionHeader title="مقایسه ناتمام ماند" />
          <div className="space-y-4 p-5">
            <Banner tone="danger">
              {state.error_message ?? 'مقایسه به نتیجه نرسید.'}
            </Banner>
            <Button variant="primary" onClick={judgment.reset}>
              شروع دوباره
            </Button>
          </div>
        </Card>
      )}

      {state !== null && state.stage === 'completed' && state.judgment !== null && (
        <div className="space-y-4">
          {state.audit_error !== null && (
            <Banner tone="warning">
              قضاوت ساخته شد اما ثبت آن در لاگ حسابرسی انجام نشد.
            </Banner>
          )}

          <JudgmentResult judgment={state.judgment} request={state.request} />

          <JudgmentExportBar judgmentId={state.judgment_id} />

          <Card>
            <SectionHeader
              title="شواهد"
              caption="بازیابی یک بار و مشترک انجام شده است؛ همین شواهد مبنای سنجش هر دو گزینه‌اند."
            />
            <div className="p-5">
              <EvidenceView
                evidence={state.evidence}
                supplementary={state.supplementary}
                examples={state.judgment.historical_examples}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

/** خروجی‌گیری قضاوت. هر دو قالب از مسیر پاک‌سازی راز عبور می‌کنند. */
const JudgmentExportBar = ({ judgmentId }: { judgmentId: string }) => {
  const [busy, setBusy] = useState<'markdown' | 'json' | null>(null)

  const save = async (format: 'markdown' | 'json'): Promise<void> => {
    setBusy(format)
    try {
      const { blob, filename } = await downloadJudgment(judgmentId, format)
      saveBlob(blob, filename)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'دریافت خروجی انجام نشد.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-surface-sunken px-4 py-3">
      <span className="me-auto text-xs text-ink-muted">خروجی برای پیوست و بایگانی:</span>
      <Button
        size="sm"
        icon={<Download className="size-4" aria-hidden />}
        loading={busy === 'markdown'}
        onClick={() => {
          void save('markdown')
        }}
      >
        Markdown
      </Button>
      <Button
        size="sm"
        icon={<FileJson className="size-4" aria-hidden />}
        loading={busy === 'json'}
        onClick={() => {
          void save('json')
        }}
      >
        JSON
      </Button>
    </div>
  )
}
